<template>
  <div class="ts-overflow-hidden">
    <ts-card>
      <ts-card-wrapper>
        <div class="row">
          <div class="col-md-2 form-group ts-px-2 ts-pt-6 ts-space-x-2">
            <div class="ts-flex ts-justify-between">
              <div class="ts-justify-start">
                <ts-button v-if="isStaff || isTeacher" class="ts-mb-1" color="info" outline
                  @click.prevent="() => (show_form = true)">{{ $t('add_new') }}
                </ts-button>
              </div>
              <div class="ts-justify-end">
                <label class="ts-mt-3">{{
                  $t('studentGallery.eventDate')
                }}</label>
              </div>
            </div>
          </div>
          <div class="col-md-2 form-group ts-px-2">
            <label class="form-col-label control-label ts-mb-2">{{
              $t('studentGallery.dateFrom')
            }}</label>
            <ts-date-picker-input :class="{ 'is-invalid': errors.has('date_from') }"
              v-model="date_from"></ts-date-picker-input>
            <div class="ivu-form-item-error ts-text-red-600" v-if="errors.has('date_from')">
              {{ errors.first('date_from') }}
            </div>
          </div>
          <div class="col-md-2 form-group ts-px-2">
            <label class="form-col-label control-label ts-mb-2">{{
              $t('studentGallery.dateTo')
            }}</label>
            <ts-date-picker-input :class="{ 'is-invalid': errors.has('date_to') }"
              v-model="date_to"></ts-date-picker-input>
            <div class="ivu-form-item-error ts-text-red-600" v-if="errors.has('date_to')">
              {{ errors.first('date_to') }}
            </div>
          </div>
          <div class="col-md-3 form-group ts-px-2"></div>
          <div class="col-md-3 form-group ts-px-2">
            <label class="form-col-label control-label ts-mb-2"></label>
            <ts-input v-model="search" :placeholder="$t('studentGallery.search')" />
          </div>
        </div>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <ts-table :columns="columns" :records="resources" :loading="loading">
          <template v-slot="{ record }">
            <td>
              {{ record.event_name }}
            </td>
            <td>
              {{ record.event_date }}
            </td>
            <td>
              <span v-for="(cla, index) in record.gallery_class" class="badge badge-primary mr-2">
                {{ cla.class_name }}</span>
            </td>
            <td>
              {{ record.student ? record.student.first_name_en + ' ' + record.student.last_name_en : '' }}
            </td>
            <td class="text-right ts-whitespace-no-wrap ts-px-8">
              <a href="#" v-tooltip="$t('view')" class="mr-2 text-info" @click.prevent="view(record)">
                <i class="fas fa-eye"></i>
              </a>
              <a href="#" class="mr-2 text-primary" @click.prevent="onEdit(record)" v-tooltip="$t('edit')">
                <i class="fas fa-edit"></i>
              </a>
              <a href="#" class="text-danger" @click.prevent="deleteRecord(record.upload_id)" :disabled="record._deleting"
                v-tooltip="$t('delete')">
                <i class="fas fa-circle-notch spin" v-if="record._deleting"></i>
                <i class="fas fa-trash" v-else></i>
              </a>
            </td>
          </template>
        </ts-table>
      </div>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination v-model="pagination" @navigate="fetchData"></ts-pagination>
      </div>
    </ts-card>
    <a-modal v-model="show_form" :title="$t('studentGallery.pageTitle')" :zIndex="1020" :footer="null"
      :maskClosable="false" width="45%" @cancel="clearInput">
      <form-component ref="form_component" @close="() => (show_form = false)" @fetchData="fetchData" />
    </a-modal>
    <a-modal v-model="show_edit" :title="$t('studentGallery.pageTitle')" :zIndex="1020" :footer="null"
      :maskClosable="false" width="40%" @cancel="clearInput">
      <edit-component ref="edit_component" @close="() => (show_edit = false)" @fetchData="fetchData" />
    </a-modal>
    <ts-loading-banner :loading="loadingPhoto">
      <a-modal v-if="view_photo" v-model="view_photo" :title="$t('studentGallery.pageTitle')" :zIndex="1020"
        :footer="null" :maskClosable="false" width="70%">
        <view-component :resource="resource_photo" ref="view_component" @close="() => (view_photo = false)"
          @fetchPhoto="fetchPhoto" />
        <div class="d-flex justify-content-end p-15">
          <ts-pagination v-model="paginationPhoto" @navigate="fetchPhoto"></ts-pagination>
        </div>
      </a-modal>
    </ts-loading-banner>
    <a-drawer :title="$t('studentGallery.info')" placement="right" :closable="false" :visible="visible"
      @close="() => (visible = false)" :zIndex="1020" width="300px">
      <info :photoItem="model" />
    </a-drawer>
  </div>
</template>

<script>

class Pagination {
  constructor(paging = {}) {
    this.currentPage = paging.currentPage ? parseInt(paging.currentPage) : 1
    this.lastPage = paging.lastPage ? parseInt(paging.lastPage) : 0
    this.perPage = paging.perPage ? parseInt(paging.perPage) : 15
    this.total = paging.total ? parseInt(paging.total) : 0
    this.from = this.perPage * (this.currentPage - 1) + 1
    this.to = this.perPage * this.currentPage
  }
}

import { Errors } from 'form-backend-validation'
import FormComponent from './form'
import EditComponent from './edit'
import ViewComponent from './view-photo.vue'
import { debounce } from 'lodash'
import info from './components/info'
import * as FileDownload from 'downloadjs'
import { mapState } from 'vuex'

export default {
  name: 'studentGallery',
  components: { FormComponent, info, EditComponent, ViewComponent },
  data() {
    return {
      errors: new Errors(),
      loading: false,
      loadingPhoto: false,
      show_form: false,
      show_edit: false,
      view_photo: false,
      visible: false,
      model: {},
      paginationPhoto: new Pagination(),
      resource_photo: [],
      upload_id: null,
      view_record: null
    }
  },
  computed: {
    ...mapState('staffStudentGallery', ['resources', 'pagination']),
    columns() {
      return [
        {
          name: this.$t('studentGallery.eventName'),
        },
        {
          name: this.$t('studentGallery.eventDate'),
        },
        {
          name: this.$t('studentGallery.className'),
        },
        {
          name: this.$t('studentGallery.studentName'),
        },
        {
          name: this.$t('studentGallery.action'),
          style: 'text-align: right;'
        },
      ]
    },
    isStaff() {
      return this.$store.getters.isStaffLogin
    },
    isTeacher() {
      return this.$store.getters.isTeacherLogin
    },
    search: {
      get() {
        return this.$store.state.staffStudentGallery.search
      },
      set(val) {
        this.$store.commit('staffStudentGallery/SET_SEARCH', val)
      }
    },
    date_from: {
      get() {
        return this.$store.state.staffStudentGallery.date_from
      },
      set(val) {
        this.$store.commit('staffStudentGallery/SET_DATE_FROM', val)
      }
    },
    date_to: {
      get() {
        return this.$store.state.staffStudentGallery.date_to
      },
      set(val) {
        this.$store.commit('staffStudentGallery/SET_DATE_TO', val)
      }
    }
  },
  methods: {
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('staffStudentGallery/fetch', attributes)
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    clearInput() {
      this.$refs.form_component.clearInput()
    },
    onEdit(items) {
      this.show_edit = true;
      this.$store.commit("staffStudentGallery/SET_EDIT_DATA", items);
      this.$refs.edit_component.setEditData();
    },
    async fetchPhoto(attributes) {
      this.loadingPhoto = true
      this.upload_id = this.view_record.upload_id
      await this.$store
        .dispatch('staffStudentGallery/viewPhoto',
          Object.assign(
            {
              upload_id: this.view_record.upload_id,
            },
            { ...attributes }))
        .then(response => {
          this.RECEIVE_RESOURCES(response)
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loadingPhoto = false
        })
    },
    RECEIVE_RESOURCES(records) {
      const { data, ...meta } = records
      this.resource_photo = data
      this.$nextTick(() => {
        this.paginationPhoto.currentPage = meta.meta.current_page
        this.paginationPhoto.lastPage = meta.meta.last_page
        this.paginationPhoto.perPage = meta.meta.per_page
        this.paginationPhoto.total = meta.meta.total
        this.paginationPhoto.from = meta.meta.from
        this.paginationPhoto.to = meta.meta.to
      })
    },
    async view(items) {
      this.view_record = items;
      await this.fetchPhoto();
      this.view_photo = true;
    },
    deleteRecord(upload_id) {
      this.loading = true
      this.$store
        .dispatch('staffStudentGallery/destroy', upload_id)
        .then(response => {
          this.loading = false
          this.fetchData()
          this.$toasted.success(response.message)
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    download() {
      FileDownload(this.model.file_path)
    },
    info() {
      this.visible = true
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('staffStudentGallery/RESET_STATE')
    next()
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchData()
    })
  },
  watch: {
    search: debounce(function () {
      this.fetchData()
    }, 500),
    date_from: function () {
      this.fetchData()
    },
    date_to: function () {
      this.fetchData()
    }
  }
}
</script>
<style scoped lang="scss"></style>
